import React, { useState } from 'react'
import { useMutation } from 'jsonapi-react'
import useSessionData from 'app/utils/useSessionData'
import useEventTracker from 'lib/useEventTracker'
import { Modal } from 'lib/modal'
import Input from 'app/theme/forms/Input'
import Radio from 'app/theme/forms/Radio'
import posthog from 'posthog-js'

export default function InquiryModal({listing, closeModal, isOpen}) {
  const [validations, setValidations] = useState({})
  const [submitted, setSubmitted] = useState(false)

  const [sessionData] = useSessionData()
  const tracker = useEventTracker()

  const [addMarketInquiry, { data, setData, errors }] = useMutation('market_inquiries', {
    initialData: {
      listing: listing,
      name: '',
      last_name: '',
      email: '',
      phone: '',
      comments: '',
      has_financing: undefined,
      meta: sessionData.meta
    }
  })

  const validate = () => {
    const v = {}
    const emailRegex = /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/

    if (!data.name) {
      v.name = 'Please enter your name'
    }
    if (!data.email) {
      v.email = 'Please enter an email address'
    } else if (!emailRegex.test(data.email)) {
      v.email = 'Please enter a valid email address'
    }
    if (!data.phone) {
      v.phone = 'Please enter your phone number'
    }
    if (data.has_financing === undefined) {
      v.has_financing = 'Please choose one'
    }

    setValidations(v)
    return !Object.keys(v).length
  }

  const formatPhone = str => {
    const clean = ('' + str).replace(/\D/g, '');
    const match = clean.match(/^(\d{3})(\d{1,3})?(\d{1,4})?(.*)/)

    if (!match) {
      return clean ? `(${clean}` : ''
    } else if (match[3]) {
      return `(${match[1]}) ${match[2]}-${match[3]}${match[4]}`
    } else if (match[2]) {
      return `(${match[1]}) ${match[2]}`
    } else {
      return `(${match[1]}) `
    }
  }

  const submitInquiry = (e) => {
    e && e.preventDefault()
    posthog.capture('truck_inquiry_submitted', { listing_id: listing.id })

    if (!validate()) {
      return
    }

    tracker.event('Submit', {
      event_category: 'Truck Inquiry Form',
    })

    addMarketInquiry(data).then(response => {
      if (response.data) {
        setSubmitted(true)
        tracker.event('Success', {
          event_category: 'Truck Inquiry Form',
        })
        posthog.capture('truck_inquiry_success', { listing_id: listing.id })
      } else if (response.error || response.errors) {
        alert('There was a problem sending your inquiry')

        tracker.event('Error', {
          event_category: 'Truck Inquiry Form',
        })
        posthog.capture('truck_inquiry_error', { listing_id: listing.id })
      }
    })
  }
  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      className="relative m-5 w-full shadow-lg self-start"
      style={{ maxWidth: 600 }}
    >
      {submitted ? (
        <Success closeModal={closeModal} />
      ) : (
        <form className="bg-gray-100" onSubmit={e => submitInquiry(e)}>
          <div className="bg-gray-200 py-4 border-b border-gray-300 px-6">
            <h3 className="font-roboto-flex text-3xl">Inquire about {listing.title}</h3>
          </div>
          <div className="m-4 md:flex">
            <label
              htmlFor="name"
              className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6"
            >
              First Name
            </label>
            <div className="w-full md:w-3/4 inline-block">
              <Input
                value={data.name}
                error={errors?.name || validations.name}
                classNames={{ input: 'w-full border rounded px-2' }}
                onChange={name => {
                  setData(prev => ({
                    ...prev,
                    name,
                  }))
                }}
              />
            </div>
          </div>
          <div className="m-4 md:flex">
            <label
              htmlFor="last_name"
              className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6"
            >
              Last Name
            </label>
            <div className="w-full md:w-3/4 inline-block">
              <Input
                value={data.last_name}
                error={errors?.last_name || validations.last_name}
                classNames={{ input: 'w-full border rounded px-2' }}
                onChange={last_name => {
                  setData(prev => ({
                    ...prev,
                    last_name,
                  }))
                }}
              />
            </div>
          </div>
          <div className="m-4 md:flex">
            <label
              htmlFor="email"
              className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6"
            >
              Email
            </label>
            <div className="w-full md:w-3/4 inline-block">
              <Input
                value={data.email}
                error={errors?.email || validations.email}
                classNames={{ input: 'w-full border rounded px-2' }}
                onChange={email => {
                  setData(prev => ({
                    ...prev,
                    email,
                  }))
                }}
              />
            </div>
          </div>
          <div className="m-4 md:flex">
            <label
              htmlFor="phone"
              className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6"
            >
              Phone Number
            </label>
            <div className="w-full md:w-3/4 inline-block">
              <Input
                value={data.phone}
                error={errors?.phone || validations.phone}
                classNames={{ input: 'w-full border rounded px-2' }}
                onChange={phone => {
                  if (phone && phone.length > (data.phone || '').length) {
                    phone = formatPhone(phone)
                  }
                  setData(prev => ({
                    ...prev,
                    phone,
                  }))
                }}
              />
            </div>
          </div>

          <div className="m-4 md:flex">
            <label
              htmlFor="comments"
              className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6"
            >
              Comments
            </label>
            <div className="w-full md:w-3/4 inline-block">
              <textarea
                name="comments"
                placeholder="Comments"
                rows={3}
                cols={10}
                className="w-full border rounded px-2"
                value={data.comments}
                onChange={event => {
                  setData({
                    ...data,
                    comments: event.target.value,
                  })
                }}
              />
            </div>
          </div>

          <div className="m-4 md:flex">
            <label className="w-full md:w-1/4 inline-block md:text-right md:pl-2 pr-6">
              Have You Secured Financing?
            </label>
            <div className="w-full md:w-3/4 inline-block">
              <div className="flex">
                <Radio
                  name="has_financing"
                  label="Yes"
                  className="p-4 pl-0"
                  checked={data.has_financing === true}
                  onChange={() => {
                    setData(prev => ({
                      ...prev,
                      has_financing: true,
                    }))
                  }}
                />
                <Radio
                  name="has_financing"
                  label="No"
                  className="p-4"
                  checked={data.has_financing === false}
                  onChange={() => {
                    setData(prev => ({
                      ...prev,
                      has_financing: false,
                    }))
                  }}
                />
              </div>

              {validations.has_financing && (
                <div className="text-sm text-red-600 mt-1">
                  {validations.has_financing}
                </div>
              )}
            </div>
          </div>
          <div
            className={[
              'border-t border-gray-300 p-8',
              'grid gap-3 md:grid-cols-2 md:gap-8',
            ]}
          >
            <button
              onClick={() => closeModal()}
              className={[
                'btn btn-gray order-last md:order-first',
                'relative py-2 text-lg flex justify-center items-center',
              ]}
            >
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              className={[
                'btn btn-white order-last md:order-first',
                'relative py-2 text-lg flex justify-center items-center',
              ]}
            >
              <span>Submit</span>
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function Success(props) {
  const { closeModal } = props

  return (
    <div className="bg-gray-100 text-center">
      <div className="bg-gray-200 py-4 border-b border-gray-300 px-6">
        <h1 className="font-roboto-flex text-3xl">Thank you! We got your info.</h1>
      </div>

      <p className="text-lg mt-4 px-10">
        Please check your email for next steps and deposit information.
        For details about the deposit and how our process works,
        please read our <a className="font-roboto text-teal" href="/marketplace/faq/">Frequently Asked Questions.</a>
        </p>

      <div
        className={[
          'p-8',
          'grid gap-3 md:grid-cols-1 md:gap-8',
        ]}
      >
        <button
          type="button"
          onClick={() => {
            closeModal()
          }}
          className={[
            'btn btn-white order-last md:order-first',
            'relative py-2 text-lg flex justify-center items-center',
          ]}
        >
          Close
        </button>
      </div>
    </div>
  )
}